import React, { ReactNode, useState } from 'react';

import { DEFAULT_FILTERS } from '@qality/common-config/src/defaultFilters';
import { Issue, TestCaseTestSteps } from '@qality/common-model';
import {
  DownloadService,
  IssueService,
  StorageOfUniqueServices,
  TestStepService,
} from '@qality/common-service';

import {
  ExportOptions,
  ExportStatus,
  ExportTestCasesContext,
} from './ExportTestCasesContext';
import { createCsvData } from './ExportTestCasesProvider.util';

export interface ExportTestCasesProviderProps {
  children: ReactNode;
}

const FILENAME = 'QAlity_exported-test-cases.csv';

export function ExportTestCasesProvider({
  children,
}: ExportTestCasesProviderProps): JSX.Element {
  const [exportStatus, setExportStatus] = useState<ExportStatus>('default');
  const [numberOfExportedIssues, setNumberOfExportedIssues] = useState(0);

  const exportTestCases = async (options: ExportOptions): Promise<void> => {
    try {
      setExportStatus('init');
      const issues = await getIssuesFromOptions(options);
      setNumberOfExportedIssues(issues.length);

      if (issues.length === 0) {
        setExportStatus('noIssues');
        return;
      }

      setExportStatus('fetchSteps');
      const steps = await getTestStepsForIssues(issues);

      setExportStatus('makeFile');
      const csvData = createCsvData(issues, steps);
      await DownloadService.downloadCSVFile(FILENAME, csvData);

      setExportStatus('success');
    } catch (error) {
      setExportStatus('error');
    }
    setExportStatus('default');
  };

  const getJQLFromUrl = async () => {
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getJiraURLSearchParams();

    const jqlQueryParam = urlSearchParams.get('jql');
    const filterQueryParam = urlSearchParams.get('filter');
    let jqlValue;

    if (jqlQueryParam) {
      jqlValue = jqlQueryParam;
    } else if (DEFAULT_FILTERS[filterQueryParam]) {
      jqlValue = DEFAULT_FILTERS[filterQueryParam];
    } else if (filterQueryParam) {
      const filterResponse = await IssueService.getFilter(filterQueryParam);
      const { jql: jqlFromFilters } = filterResponse;
      if (jqlFromFilters) {
        jqlValue = decodeURIComponent(jqlFromFilters);
      }
    } else {
      jqlValue = DEFAULT_FILTERS.default;
    }
    return jqlValue;
  };

  const getTestStepsForIssues = (
    issues: Array<Issue>
  ): Promise<TestCaseTestSteps[]> => {
    return TestStepService.bulkFetchTestSteps(issues.map((i) => i.id));
  };

  const getIssuesFromOptions = async (options: ExportOptions) => {
    switch (options.source) {
      case 'issues': {
        return options.issues;
      }
      case 'url': {
        const jql = await getJQLFromUrl();
        return await IssueService.getAllIssuesByJqlFilteredByQAlityType({
          jql,
          fields: ['summary', 'description', 'issuetype'],
        });
      }
      default: {
        throw new Error('Invalid export source argument!');
      }
    }
  };

  return (
    <ExportTestCasesContext.Provider
      value={{
        numberOfExportedIssues,
        exportStatus,
        exportTestCases,
      }}
    >
      {children}
    </ExportTestCasesContext.Provider>
  );
}
